import React from 'react'
import PropTypes from 'prop-types'
import { Button } from '../../group_leader/components/common'
import { makeT } from '../../group_leader/helpers/i18n'

const t = makeT('group_leader.event_results')

// Live / finalized / not started; each says it in words, so colour is never the only signal.
const StatusBadge = ({ isLive, isComplete }) => {
  if (isLive) {
    return (
      <div className="gl-event-results-live-badge">
        <span className="gl-event-results-live-dot" aria-hidden="true"/>
        {t('status.live')}
      </div>
    )
  }
  if (isComplete) {
    return <div className="gl-event-results-completed-badge">{t('status.completed')}</div>
  }
  return <div className="gl-event-results-status-pill">{t('status.not_started')}</div>
}

StatusBadge.propTypes = {
  isLive: PropTypes.bool,
  isComplete: PropTypes.bool,
}

// A toolbar link, shown only when the page has its URL.
const linkButton = (href, action, variant = 'secondary') => href && (
  <Button variant={variant} size="md" href={href} ariaLabel={t(`actions.${action}_aria`)}>
    {t(`actions.${action}`)}
  </Button>
)

const ResultsHeader = ({
  title,
  date,
  isLive,
  isComplete,
  scoreEntryUrl,
  eventSetupUrl,
  leagueLbUrl,
  finalizing,
  onFinalize,
  onSendScoringLinks,
  sendingLinks,
}) => (
  <div className="gl-event-results-page-hd">
    {/* Grid items placed by name, so a phone rewraps without moving tab order. */}
    <div className="gl-event-results-hd-top">
      <h1 className="gl-event-results-hd-title">{title}</h1>
      {date && <div className="gl-event-results-hd-meta">{date}</div>}
      <div className="gl-event-results-hd-status">
        <StatusBadge isLive={isLive} isComplete={isComplete}/>
      </div>
      <div className="gl-event-results-hd-finalize">
        {isComplete ? (
          <Button variant="secondary" size="md" disabled>
            {`✓ ${t('actions.finalized_label')}`}
          </Button>
        ) : (
          <Button
            variant="outlinePrimary"
            size="md"
            onClick={onFinalize}
            disabled={finalizing}
            ariaLabel={t('actions.finalize_aria')}
            >
            {finalizing ? t('actions.finalizing') : t('actions.finalize')}
          </Button>
        )}
      </div>
    </div>

    {/* Ordered by the "what happens next" steps; scoring mostly happens in the mobile app. */}
    <div className="gl-event-results-hd-toolbar">
      {linkButton(eventSetupUrl, 'edit_event', 'primary')}
      {eventSetupUrl && <span className="gl-event-results-hd-divider" aria-hidden="true"/>}
      {onSendScoringLinks && (
        <Button
          variant="secondary"
          size="md"
          onClick={onSendScoringLinks}
          disabled={sendingLinks}
          >
          {sendingLinks ? t('actions.sending_scoring_links') : t('actions.send_scoring_links')}
        </Button>
      )}
      {linkButton(scoreEntryUrl, 'enter_scores')}
      {linkButton(leagueLbUrl, 'view_leaderboard')}
    </div>
  </div>
)

ResultsHeader.propTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.string,
  isLive: PropTypes.bool,
  isComplete: PropTypes.bool,
  scoreEntryUrl: PropTypes.string,
  eventSetupUrl: PropTypes.string,
  leagueLbUrl: PropTypes.string,
  finalizing: PropTypes.bool,
  onFinalize: PropTypes.func.isRequired,
  onSendScoringLinks: PropTypes.func,
  sendingLinks: PropTypes.bool,
}

export default ResultsHeader
